import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { isNative } from './Platform';

/**
 * Capgo live update wrapper.
 *
 * `notifyAppReady()` is the safety net: if we don't call it within ~10s of
 * boot, Capgo auto-reverts to the previous bundle on the next launch — so
 * a broken JS update cannot brick the app.
 *
 * On resume we ping `sync()` — but only on a real network — so users don't
 * waste mobile data re-downloading on every foreground. The plugin itself
 * also polls according to `capacitor.config.ts`, this is belt-and-suspenders.
 *
 * OTA bundles may only change JS/UI. Anything that touches native code or
 * adds a new permission MUST go through a regular App Store / Play Store
 * release or Apple can pull the app.
 */

let resumeHooked = false;
let readyCalled = false;

async function notifyReady() {
    if (!isNative()) return;
    if (readyCalled) return;
    try {
        await CapacitorUpdater.notifyAppReady();
        readyCalled = true;
    } catch (err) {
        console.warn('[live-updates] notifyAppReady failed', err);
    }
}

async function syncIfOnline() {
    if (!isNative()) return;
    try {
        const status = await Network.getStatus();
        if (!status.connected) return;
        await CapacitorUpdater.sync();
    } catch (err) {
        console.warn('[live-updates] sync failed', err);
    }
}

function hookResume() {
    if (resumeHooked || !isNative()) return;
    resumeHooked = true;
    App.addListener('resume', () => {
        syncIfOnline();
    });
}

/**
 * Call once from App.vue `mounted()`. Safe to call on web — no-ops.
 */
function init() {
    if (!isNative()) return;
    hookResume();
    // Defer the ready-signal until the app has actually rendered and the
    // user's first route is on screen. If the boot crashes before this
    // tick runs, Capgo rolls back — which is exactly what we want.
    setTimeout(() => {
        notifyReady();
    }, 0);
}

/**
 * Expose a manual trigger so a "check for updates" button in Settings can
 * request an immediate sync. Resolves with Capgo's response or null on web.
 */
async function checkForUpdates() {
    if (!isNative()) return null;
    try {
        return await CapacitorUpdater.sync();
    } catch (err) {
        console.warn('[live-updates] manual sync failed', err);
        return null;
    }
}

async function currentBundle() {
    if (!isNative()) return null;
    try {
        return await CapacitorUpdater.current();
    } catch (err) {
        console.warn('[live-updates] current() failed', err);
        return null;
    }
}

export default {
    init,
    notifyReady,
    checkForUpdates,
    currentBundle
};
